import { CalendarDays, Sun, Sunset, X } from "lucide-react";
import { format, addDays, startOfToday } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { BOOKING } from "@/data/site";

const SLOT_ICONS = { morning: Sun, afternoon: Sunset };

export const slotLabel = (slotId) => {
  const slot = BOOKING.slots.find((s) => s.id === slotId);
  return slot ? `${slot.label} (${slot.hours})` : null;
};

export const formatPreferred = (date, slotId) => {
  const slot = BOOKING.slots.find((s) => s.id === slotId);
  if (!date && !slot) return "Flexible";
  const parts = [];
  if (date) parts.push(format(date, "EEE d MMM"));
  if (slot) parts.push(slot.label);
  return parts.join(" · ");
};

/** Preferred date + time-of-day picker (Mon–Sat, confirmed manually by Warren). */
export default function BookingPicker({ date, slot, onDateChange, onSlotChange }) {
  const today = startOfToday();
  const isDisabled = (d) =>
    d < today ||
    d > addDays(today, BOOKING.daysAhead) ||
    !BOOKING.workingDays.includes(d.getDay());

  return (
    <div
      data-testid="quote-booking"
      className="border border-white/8 bg-[#0E0E0E] p-5 md:p-7"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
          Step 04 · Preferred time (optional)
        </p>
        <CalendarDays className="w-4 h-4 text-white/40" />
      </div>
      <p className="text-white/45 text-xs mb-5">
        Mon – Sat, 8am – 5pm. Pick what suits — Warren confirms the exact slot with you.
      </p>

      <div className="grid md:grid-cols-2 gap-5 items-start">
        <div className="border border-white/8 bg-[#0A0A0A] flex justify-center">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => onDateChange(d || null)}
            disabled={isDisabled}
            fromMonth={today}
            toMonth={addDays(today, BOOKING.daysAhead)}
            weekStartsOn={1}
            className="booking-calendar"
            classNames={{
              caption_label: "text-sm font-display tracking-wide text-white",
              nav_button:
                "h-7 w-7 border border-white/10 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] inline-flex items-center justify-center transition",
              head_cell: "w-9 text-[10px] uppercase tracking-[0.2em] text-white/40 font-normal",
              cell: "p-0 text-center",
              day: "h-9 w-9 text-sm text-white/80 hover:bg-[#D4AF37]/15 hover:text-white transition inline-flex items-center justify-center",
              day_selected:
                "bg-[#D4AF37] text-black hover:bg-[#D4AF37] hover:text-black font-semibold",
              day_today: "border border-[#D4AF37]/50",
              day_disabled: "!text-white/15 hover:!bg-transparent cursor-not-allowed",
              day_outside: "!text-white/15",
            }}
          />
        </div>

        <div className="space-y-3">
          {BOOKING.slots.map((s) => {
            const Icon = SLOT_ICONS[s.id] || Sun;
            const active = slot === s.id;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`quote-slot-${s.id}`}
                onClick={() => onSlotChange(active ? null : s.id)}
                className={`w-full text-left p-4 border flex items-center gap-4 transition-all duration-300 ${
                  active
                    ? "border-[#D4AF37] bg-[#D4AF37]/8"
                    : "border-white/8 hover:border-white/30"
                }`}
              >
                <span
                  className={`w-10 h-10 border flex items-center justify-center shrink-0 ${
                    active ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-white/50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span>
                  <span
                    className={`font-display text-lg block leading-tight ${
                      active ? "text-[#D4AF37]" : "text-white"
                    }`}
                  >
                    {s.label}
                  </span>
                  <span className="text-white/45 text-xs">{s.hours}</span>
                </span>
              </button>
            );
          })}

          <div className="flex items-center justify-between pt-2 text-xs">
            <span className="text-white/45">
              Preferred:{" "}
              <span data-testid="quote-preferred-label" className="text-white">
                {formatPreferred(date, slot)}
              </span>
            </span>
            {(date || slot) && (
              <button
                type="button"
                data-testid="quote-booking-clear"
                onClick={() => {
                  onDateChange(null);
                  onSlotChange(null);
                }}
                className="flex items-center gap-1 text-white/45 hover:text-[#D4AF37] transition"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
